/**
 * The course a bank question belongs to, and which categories that course has.
 *
 * A question in the bank is written for a course, and a course teaches some
 * subject areas, not all of them. So composing one in the bank starts by
 * naming the course; the category choice that follows is that course's.
 * Inside an assessment the course is already known and nothing is asked.
 *
 * A course is a top-level bank folder. Its subfolders are its subject areas,
 * and they carry the same names as the category domains, which is how a
 * course's categories are found.
 */

import { COURSE_CONTENT_DIMENSION_ID } from "./dimensions"
import { getDescendantCategoryIds } from "./tree"
import type { ExamCategory } from "./types"

type FolderLike = { id: string; name: string; parentId: string | null }

/** Top-level folders that are courses. Favorites is a view, not a course. */
export function courseFolders<T extends FolderLike>(folders: readonly T[]): T[] {
  return folders.filter(
    (folder) => folder.parentId === null && folder.id !== "fld-favorites",
  )
}

/** The course a folder sits in, or nothing when it sits outside any. */
export function courseOfFolder<T extends FolderLike>(
  folders: readonly T[],
  folderId: string | undefined,
): T | undefined {
  let current = folders.find((folder) => folder.id === folderId)
  while (current?.parentId) {
    current = folders.find((folder) => folder.id === current!.parentId)
  }
  return current && current.parentId === null && current.id !== "fld-favorites"
    ? current
    : undefined
}

const norm = (value: string) => value.trim().toLowerCase()

/**
 * Every course-content category this course teaches. Empty when its subject
 * areas do not line up with the category domains, so a course the data does
 * not describe shows the whole tree instead of an empty one.
 */
export function categoryIdsForCourse(
  folders: readonly FolderLike[],
  categories: readonly ExamCategory[],
  courseId: string | undefined,
): string[] {
  if (!courseId) return []
  const areas = new Set(
    folders.filter((folder) => folder.parentId === courseId).map((f) => norm(f.name)),
  )
  if (areas.size === 0) return []
  const roots = categories.filter(
    (category) =>
      category.parentId === null &&
      (category.dimensionId ?? COURSE_CONTENT_DIMENSION_ID) ===
        COURSE_CONTENT_DIMENSION_ID &&
      areas.has(norm(category.name)),
  )
  return roots.flatMap((root) => [
    root.id,
    ...getDescendantCategoryIds([...categories], root.id),
  ])
}

/**
 * The course code a bank course folder stands for ("NURS-210 Clinical Sciences I"
 * is NURS-210). Course trees are owned by this code, so the bank and the course
 * page agree on whose tree it is.
 */
export function courseCodeOfFolderName(name: string | undefined): string | undefined {
  const match = name?.trim().match(/^([A-Za-z]+-?\s?\d+[A-Za-z]?)\b/)
  return match ? match[1]!.toUpperCase().replace(/\s+/g, "-") : undefined
}
